import { useMemo, useState } from 'react';
import {
  Box, Typography, Button, Chip, Stack,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteProject } from '../../../api/hooks/useProjects';
import { useNotifications } from '../../../context/NotificationContext';
import { ProjectFormDialog } from './ProjectFormDialog';
import { EnhancedDataTable, type ColumnDef } from '../../../components/common/EnhancedDataTable';
import { ListActionsColumn } from '../../../components/common/ListActionsColumn';
import { ApiError } from '../../../api/client';

export interface ProjectRow {
  id: string;
  project_no: string | null;
  name: string;
  state: string;
  priority: string;
  bill_type: string | null;
  deadline: string | null;
  /** Projektleiter (Verantwortlicher). Kommt aus GET /projects?include=lead;
   *  coworker_id ist null beim Freitext-Fallback, name ist null wenn kein Leiter. */
  lead_id: string | null;
  lead_name: string | null;
}

const STATE_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  PLANNED: 'default', STARTED: 'info', IN_PROGRESS: 'warning', COMPLETED: 'success', CANCELED: 'error',
};
const PRIORITY_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default', NORMAL: 'info', HIGH: 'warning', URGENT: 'error',
};

/** Stabiler Filter-Schlüssel je Projektleiter: bevorzugt coworker_id (stabil,
 *  disambiguiert Gleichnamige), sonst name-basierter Fallback für Freitext-
 *  Leiter ohne coworker_id. Leerer String = kein Leiter (nicht filterbar). */
function leadKey(leadId: string | null, leadName: string | null): string {
  if (leadId) return leadId;
  const n = leadName?.trim();
  return n ? `name:${n}` : '';
}

/**
 * Projekt-Tabelle für die Programm-HQ-Seite. Erhält die bereits (durch den
 * gemeinsamen Filter oben) gefilterten Projekte und rendert sie clientseitig
 * (Sortierung + Offset-Pagination via EnhancedDataTable). Die Spaltenfilter für
 * Name/Status/Priorität sind deaktiviert — diese Filter liegen oben gemeinsam
 * mit der Timeline. Anlegen/Bearbeiten/Löschen bleiben erhalten.
 */
export function ProjectsTable({ projects, loading }: { projects: ProjectRow[]; loading: boolean }) {
  const { t } = useTranslation(['projects', 'common']);
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const [createOpen, setCreateOpen] = useState(false);
  // Tabellen-eigener Filter nach Projektleiter (wirkt NUR auf die Tabelle, nicht
  // auf die gemeinsame Filterleiste/Timeline). Schlüssel = Leiter-Name.
  const [leadFilter, setLeadFilter] = useState('');
  const deleteMutation = useDeleteProject();

  // Vorkommende Projektleiter (distinct, sortiert) für das Dropdown.
  // Schlüssel = coworker_id (stabil, vom Backend empfohlen), Fallback auf
  // name für Freitext-Leiter ohne coworker_id. Anzeige = Name (sonst id).
  const leadOptions = useMemo(() => {
    const map = new Map<string, string>(); // key -> label
    for (const p of projects) {
      const key = leadKey(p.lead_id, p.lead_name);
      if (!key) continue;
      const label = p.lead_name?.trim() || p.lead_id || key;
      if (!map.has(key)) map.set(key, label);
    }
    return Array.from(map, ([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [projects]);

  // Falls der gewählte Leiter nach einer Datenaktualisierung nicht mehr vorkommt,
  // den Filter clientseitig ignorieren (kein leerer Tabellenzustand durch Altwert).
  const activeLead = leadFilter && leadOptions.some((o) => o.key === leadFilter) ? leadFilter : '';
  const displayed = useMemo(
    () => (activeLead ? projects.filter((p) => leadKey(p.lead_id, p.lead_name) === activeLead) : projects),
    [projects, activeLead],
  );

  const handleDelete = async (row: ProjectRow) => {
    try {
      await deleteMutation.mutateAsync(row.id);
      notify(t('common:success.deleted', { defaultValue: 'Eintrag gelöscht' }), 'success');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : t('common:errors.generic');
      notify(message, 'error');
    }
  };

  const columns: ColumnDef<ProjectRow>[] = [
    { id: 'project_no', header: t('projects:project.projectNo'), accessor: 'project_no', minWidth: 100, sortable: true, filterable: false },
    { id: 'name', header: t('projects:project.name'), accessor: 'name', minWidth: 220, sortable: true, filterable: false },
    {
      id: 'lead_name', header: t('projects:project.lead', 'Projektleiter'), accessor: 'lead_name', minWidth: 160, sortable: true, filterable: false,
      render: (value) => (value ? String(value) : '—'),
    },
    {
      id: 'state', header: t('projects:project.state'), accessor: 'state', minWidth: 130, sortable: true, filterable: false,
      render: (value) => <Chip label={t(`projects:states.${String(value)}`)} color={STATE_COLORS[String(value)] ?? 'default'} size="small" />,
    },
    {
      id: 'priority', header: t('projects:project.priority'), accessor: 'priority', minWidth: 120, sortable: true, filterable: false, defaultHidden: true,
      render: (value) => <Chip label={t(`projects:priorities.${String(value)}`)} color={PRIORITY_COLORS[String(value)] ?? 'default'} size="small" variant="outlined" />,
    },
    {
      id: 'bill_type', header: t('projects:project.billType'), accessor: 'bill_type', minWidth: 130, filterable: false, defaultHidden: true,
      render: (value) => (value ? t(`projects:billTypes.${String(value)}`) : '—'),
    },
    {
      id: 'deadline', header: t('projects:project.deadline'), accessor: 'deadline', minWidth: 110, sortable: true,
      render: (value) => (value ? new Date(String(value)).toLocaleDateString('de-DE') : '—'),
    },
    {
      id: 'actions', header: '', accessor: () => null, minWidth: 120, sortable: false, filterable: false, hideable: false,
      render: (_value, row) => (
        <ListActionsColumn
          row={row}
          onEdit={(r) => navigate(`/projects/${r.id}/edit`)}
          onDelete={handleDelete}
          deleteTitle={t('projects:delete.confirmTitle')}
          deleteMessage={t('projects:delete.confirmMessage')}
        />
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">{t('projects:timeline.projectsHeading', 'Alle Projekte im Überblick')}</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {t('projects:project.create')}
        </Button>
      </Stack>

      {/* Tabellen-eigener Projektleiter-Filter (nur Tabelle). Nur sichtbar, wenn
          überhaupt Projektleiter in den Daten vorkommen. */}
      {leadOptions.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="lead-filter-label" shrink>{t('projects:project.lead', 'Projektleiter')}</InputLabel>
            <Select
              labelId="lead-filter-label"
              id="lead-filter"
              value={activeLead}
              input={<OutlinedInput notched label={t('projects:project.lead', 'Projektleiter')} />}
              onChange={(e) => setLeadFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>{t('projects:project.allLeads', 'Alle Projektleiter')}</em></MenuItem>
              {leadOptions.map((o) => (
                <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <EnhancedDataTable
        tableId="project-list"
        columns={columns}
        data={displayed}
        loading={loading}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate(`/projects/${row.id}`)}
        exportFilename="projekte"
        emptyState={{
          title: t('projects:list.empty.title'),
          hint: t('projects:list.empty.hint'),
          icon: <FolderOpenIcon sx={{ fontSize: 48, color: 'text.secondary' }} />,
          action: (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              {t('projects:project.create')}
            </Button>
          ),
        }}
      />

      <ProjectFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
